import { Tab } from 'tab-election'
import { SubscribedRef, websocketFilterManager } from './services/websocket-filter-manager'

export const UPDATE_SUBSCRIPTION_REFS_TOPIC = 'updateSubscriptionRefs'
export const TOPIC_REQUEST_SUBSCRIPTIONS = 'request:subscriptions'
export const TOPIC_TAB_OPENED = 'tab:opened'
export const TOPIC_TAB_CLOSED = 'tab:closed'

export type HubHandler<T = unknown> = (payload: T) => void

type MessageData = {
    topic: string
    payload: unknown
    metadata: { fromTabId: string }
}

type SubscriptionRequest = {
    requestId: string
}

type SubscriptionResponse = {
    requestId: string
    subscriptions: SubscribedRef[]
    fromTabId?: string
}

type TabLifecycleMessage = {
    tabId: string
}

export class Hub {
    private readonly tab: Tab
    private readonly topicHandlers = new Map<string, Set<HubHandler>>()
    private readonly registeredServices = new Set<string>()
    private readonly activeTabs = new Set<string>()

    constructor(namespace: string) {
        this.tab = new Tab(namespace)
        this.setupMessageHandling()
    }

    private setupMessageHandling(): void {
        this.tab.addEventListener('message', (evt: MessageEvent<MessageData>): void => {
            const { topic, payload, metadata } = evt.data || {}
            if (!topic) return

            switch (topic) {
                case UPDATE_SUBSCRIPTION_REFS_TOPIC:
                    if (metadata?.fromTabId) {
                        const response = payload as Partial<SubscriptionResponse>
                        if (response.subscriptions && response.requestId) {
                            // This is a response to a subscription request - dispatch to handlers
                            this.topicHandlers.get(UPDATE_SUBSCRIPTION_REFS_TOPIC)?.forEach((handler) => {
                                try {
                                    handler({ ...response, fromTabId: metadata.fromTabId } as SubscriptionResponse)
                                } catch (err) {
                                    // Ignore handler errors
                                }
                            })
                        } else if (this.isLeader()) {
                            // Regular subscription update
                            websocketFilterManager.updateSubscriptionRefs(metadata.fromTabId, payload as SubscribedRef[])
                        }
                    }
                    break
                case TOPIC_REQUEST_SUBSCRIPTIONS:
                    if (!this.isLeader()) {
                        const request = payload as SubscriptionRequest
                        const subs = websocketFilterManager.getCurrentSubscriptions()
                        this.tab.send({
                            topic: UPDATE_SUBSCRIPTION_REFS_TOPIC,
                            metadata: { fromTabId: this.tab.id },
                            payload: { requestId: request.requestId, subscriptions: subs },
                        })
                    }
                    break
                case TOPIC_TAB_OPENED: {
                    this.activeTabs.add((payload as TabLifecycleMessage).tabId)
                    break
                }
                case TOPIC_TAB_CLOSED: {
                    const tabId = (payload as TabLifecycleMessage).tabId
                    this.activeTabs.delete(tabId)
                    if (this.isLeader()) {
                        websocketFilterManager.clearSubscriptionsForTab(tabId)
                    }
                    break
                }
                default: {
                    const handlers = this.topicHandlers.get(topic)
                    handlers?.forEach((handler) => {
                        try {
                            handler(payload)
                        } catch (err) {
                            // Ignore handler errors
                        }
                    })
                    break
                }
            }
        })

        // Announce this tab and setup cleanup
        this.activeTabs.add(this.tab.id)
        this.publish(TOPIC_TAB_OPENED, { tabId: this.tab.id })

        if (typeof window !== 'undefined') {
            const announceClose = (): void => this.publish(TOPIC_TAB_CLOSED, { tabId: this.tab.id })
            window.addEventListener('beforeunload', announceClose)
            window.addEventListener('pagehide', announceClose)
        }
    }

    public register(service: Service): void {
        if (this.registeredServices.has(service.namespace)) return

        this.registeredServices.add(service.namespace)
        this.tab.waitForLeadership(async (): Promise<Record<string, unknown>> => {
            // Initialize service
            await service.init(this)

            // Commit leadership
            this.tab.setState({ leader: this.tab.id })

            // Collect state from known tabs before taking leadership
            await this.collectStateFromActiveTabs()

            return {}
        })
    }

    public subscribe<T = unknown>(topic: string, handler: HubHandler<T>): () => void {
        const handlers = this.topicHandlers.get(topic) || new Set<HubHandler<T>>()
        handlers.add(handler)
        this.topicHandlers.set(topic, handlers as Set<HubHandler>)

        return (): void => {
            const set = this.topicHandlers.get(topic)
            if (set) {
                set.delete(handler as HubHandler)
                if (set.size === 0) this.topicHandlers.delete(topic)
            }
        }
    }

    public publish<T = unknown>(topic: string, payload: T): void {
        this.tab.send({ topic, payload })

        // Dispatch locally
        const handlers = this.topicHandlers.get(topic)
        handlers?.forEach((handler) => {
            try {
                handler(payload)
            } catch (err) {
                // Ignore handler errors
            }
        })
    }

    public isLeader(): boolean {
        return this.tab.getState().leader === this.tab.id
    }

    public getTab(): Tab {
        return this.tab
    }

    private async collectStateFromActiveTabs(): Promise<void> {
        // Get other active tabs (excluding current)
        const otherTabs = Array.from(this.activeTabs).filter((id) => id !== this.tab.id)
        if (otherTabs.length === 0) return

        // Create unique request ID and response collector
        const requestId = `${this.tab.id}-${Date.now()}`
        const responses = new Map<string, SubscribedRef[]>()

        // Temporary handler to collect responses from other tabs
        const cleanup = this.subscribe(UPDATE_SUBSCRIPTION_REFS_TOPIC, (data: SubscriptionResponse) => {
            // Handle responses to our request (payload has requestId and subscriptions)
            if (data.requestId === requestId && data.subscriptions) {
                const fromTabId = data.fromTabId
                if (fromTabId) {
                    responses.set(fromTabId, data.subscriptions)
                }
            }
        })

        try {
            // Request subscriptions from all tabs
            this.publish(TOPIC_REQUEST_SUBSCRIPTIONS, { requestId })

            // Wait for responses with timeout (max 1s)
            await new Promise<void>((resolve) => {
                let timer: NodeJS.Timeout | undefined
                const interval = setInterval(() => {
                    // Resolve early if all tabs responded
                    if (otherTabs.every((tabId) => responses.has(tabId))) {
                        if (timer) {
                            clearTimeout(timer)
                        }
                        clearInterval(interval)
                        resolve()
                    }
                }, 100)

                timer = setTimeout(() => {
                    clearInterval(interval)
                    resolve()
                }, 1000)
            })

            // Add our own subscriptions to the collected responses
            const ownSubscriptions = websocketFilterManager.getCurrentSubscriptions()
            if (ownSubscriptions.length > 0) {
                responses.set(this.tab.id, ownSubscriptions)
            }

            // Batch update all collected subscriptions in one websocket call
            websocketFilterManager.batchUpdateSubscriptionRefs(responses)
        } finally {
            cleanup()
        }
    }
}

export interface Service {
    readonly namespace: string
    init(hub: Hub): Promise<void> | void
}
